import { useCallback } from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EventSearchForm } from '@mark43/cad-api';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../action-broadcaster/use-integrated-action-broadcaster';
import { prepareForSubmit } from './utils';
import { eventSearchFormDefaultValues } from './configuration';
import {
    locationFormDefaultValues,
    personFormDefaultValues,
    vehicleFormDefaultValues,
} from '../search-windows/configuration';

export function useEventSearchBroadcaster(
    consumer: (options: { search: EventSearchForm }) => void,
    setShowSavedSearches: (showSavedSearches: boolean) => void
) {
    const { subpremiseSupport } = useEnabledFeatures();

    const actionHandler: ActionHandler<'EVENT_SEARCH_V2'> = useCallback(
        (action) => {
            if (action.type === 'SEARCH_EVENTS_BY_PHONE_NUMBERS') {
                consumer({
                    search: prepareForSubmit({
                        ...eventSearchFormDefaultValues,
                        fuzzyMatchingEnabled: false,
                        persons: [
                            ...action.payload.phoneNumbers.map((phoneNumber) => ({
                                ...personFormDefaultValues,
                                phoneNumber,
                            })),
                        ],
                    }),
                });
            } else if (action.type === 'SEARCH_EVENTS_BY_LOCATION') {
                const location = action.payload.location;
                consumer({
                    search: prepareForSubmit({
                        ...eventSearchFormDefaultValues,
                        fuzzyMatchingEnabled: false,
                        locations: [
                            {
                                ...locationFormDefaultValues,
                                streetAddress: location?.streetAddress,
                                subPremise: subpremiseSupport
                                    ? undefined
                                    : location?.entityLinks[0]?.subPremise,
                                subPremiseDtos: subpremiseSupport
                                    ? [
                                          {
                                              subPremiseName: location?.subPremise1Name,
                                              subPremiseValue: location?.subPremise1Value,
                                          },
                                          {
                                              subPremiseName: location?.subPremise2Name,
                                              subPremiseValue: location?.subPremise2Value,
                                          },
                                          {
                                              subPremiseName: location?.subPremise3Name,
                                              subPremiseValue: location?.subPremise3Value,
                                          },
                                          {
                                              subPremiseName: location?.subPremise4Name,
                                              subPremiseValue: location?.subPremise4Value,
                                          },
                                          {
                                              subPremiseName: location?.subPremise5Name,
                                              subPremiseValue: location?.subPremise5Value,
                                          },
                                      ].filter((subpremiseDto) => !!subpremiseDto.subPremiseValue)
                                    : [],
                                city: location?.locality,
                                zip: location?.postalCode,
                                intersectionStreet1: location?.crossStreet1,
                                intersectionStreet2: location?.crossStreet2,
                            },
                        ],
                    }),
                });
            } else if (action.type === 'SEARCH_EVENTS_BY_VEHICLES') {
                consumer({
                    search: prepareForSubmit({
                        ...eventSearchFormDefaultValues,
                        fuzzyMatchingEnabled: false,
                        vehicles: [
                            ...action.payload.vehicles.map((vehicle) => ({
                                ...vehicleFormDefaultValues,
                                plateNumber: vehicle.plateNumber,
                                vinNumber: vehicle.vinNumber,
                                state: vehicle.stateOption,
                            })),
                        ],
                    }),
                });
            } else if (action.type === 'SEARCH_EVENTS_BY_REPORTING_PARTIES') {
                consumer({
                    search: prepareForSubmit({
                        ...eventSearchFormDefaultValues,
                        fuzzyMatchingEnabled: false,
                        persons: [
                            ...action.payload.reportingParties.map((person) => ({
                                ...personFormDefaultValues,
                                firstName: person.firstName,
                                lastName: person.lastName,
                                driversLicenseNumber: person.driversLicenseNumber,
                            })),
                        ],
                    }),
                });
            } else if (action.type === 'OPEN_SAVED_SEARCHES') {
                setShowSavedSearches(true);
            }
            return true;
        },
        [consumer, subpremiseSupport, setShowSavedSearches]
    );
    useIntegratedActionBroadcaster({
        id: 'EVENT_SEARCH_WINDOW',
        windowType: 'EVENT_SEARCH_V2',
        handler: actionHandler,
        ignoreActiveWorkspaceWindowType: true,
    });
}
